import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { formatDateTime } from '../lib/datetime'
import { usePageTitle } from '../lib/usePageTitle'

/**
 * "Vilka har lämnat in?"
 *
 * Visar deltagande, aldrig innehåll. Vem som är klar, vem som börjat och vem
 * som inte rört sig — plus rankingsumman för de inlämnade. Laguppställningar
 * styrs fortfarande av teams_revealed() och syns först vid tee-off.
 *
 * Listan är sorterad med de klara överst. Man läser den uppifrån när man vill
 * veta hur långt det gått, och nerifrån när man ska påminna någon.
 */
interface Row {
  user_id: string
  display_name: string
  group_name: string | null
  has_team: boolean
  submitted: boolean
  submitted_at: string | null
  picks_count: number
  rank_sum: number | null
  is_me: boolean
}

interface RevealedPlayer {
  slot: number
  full_name: string
  country: string | null
  rank_at_pick: number | null
}

export function Submissions() {
  const { id = '' } = useParams()
  const [rows, setRows] = useState<Row[]>([])
  const [name, setName] = useState<string>('')
  const [teams, setTeams] = useState<Map<string, RevealedPlayer[]>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  usePageTitle(name ? `Inlämnade lag ${name}` : 'Inlämnade lag')

  const load = useCallback(async () => {
    const [t, r, lb] = await Promise.all([
      supabase.from('tournaments').select('name').eq('id', id).maybeSingle(),
      supabase.rpc('submission_board', { p_tournament_id: id }),
      // Laguppställningarna. RLS (teams_revealed) släpper bara igenom det
      // egna laget före tee-off, allas efter — så frågan kan alltid ställas
      // och det som kommer tillbaka är exakt det som får visas.
      supabase.from('team_leaderboard').select('user_id, players').eq('tournament_id', id),
    ])
    if (t.data) setName((t.data as { name: string }).name)
    if (r.error) setError(r.error.message)
    else setRows((r.data as Row[]) ?? [])

    if (!lb.error) {
      const next = new Map<string, RevealedPlayer[]>()
      for (const row of (lb.data ?? []) as Array<{ user_id: string; players: RevealedPlayer[] }>) {
        if (row.players?.length > 0) {
          next.set(row.user_id, [...row.players].sort((a, b) => a.slot - b.slot))
        }
      }
      setTeams(next)
    }
    setLoading(false)
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  const klara = rows.filter((r) => r.submitted)
  const saknas = rows.filter((r) => !r.submitted)

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">{name || 'Tävlingen'}</p>
        <h1>Inlämnade lag</h1>
      </div>

      {error && <div className="notice notice--error">{error}</div>}
      {loading && <p className="muted">Hämtar…</p>}

      {!loading && rows.length > 0 && (
        <div className="card stack">
          <p className="muted">
            {klara.length} av {rows.length} har lämnat in.
            {saknas.length > 0 && ` ${saknas.length} kvar.`}
          </p>
          <p className="muted">
            {teams.size > 1
              ? 'Tävlingen har startat — laguppställningarna är avslöjade och står under respektive namn.'
              : 'Ett inlämnat lag har passerat regelkontrollen — den går inte att komma förbi. Vilka spelare någon valt syns först vid tee-off.'}
          </p>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="card">
          <p className="muted">
            Ingen att visa än. Skapa ett gäng så syns alla medlemmar här, även de
            som inte lämnat in.
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="board">
          <div className="marathon__head">
            <span className="board__name">Deltagare</span>
            <span className="marathon__cell">Läge</span>
            <span className="marathon__score">Ranking</span>
          </div>

          {rows.map((r) => {
            const revealed = teams.get(r.user_id)
            return (
              <div
                key={r.user_id}
                className={`marathon__row${r.is_me ? ' marathon__row--me' : ''}`}
              >
                <span className="board__name">
                  {r.display_name}
                  {r.group_name && <span className="board__player-meta"> · {r.group_name}</span>}
                  {/* Laguppställningen, när RLS avslöjat den vid tee-off.
                      Här bara namnen — scorer och positioner hör hemma på
                      leaderboarden. */}
                  {revealed && (
                    <span className="board__player-meta" style={{ display: 'block' }}>
                      {revealed.map((p) => p.full_name).join(' · ')}
                    </span>
                  )}
                </span>

                <span className="marathon__cell">
                  {r.submitted ? (
                    <span className="tag tag--ok" title={formatDateTime(r.submitted_at)}>
                      Inlämnat
                    </span>
                  ) : r.picks_count > 0 ? (
                    <span className="tag tag--wip">Påbörjat {r.picks_count}</span>
                  ) : (
                    <span className="tag">Inte börjat</span>
                  )}
                </span>

                <span className="marathon__score">{r.rank_sum ?? '–'}</span>
              </div>
            )
          })}
        </div>
      )}

      <div className="card">
        <p className="muted">
          <Link to={`/turnering/${id}/leaderboard`}>Till leaderboarden</Link>
        </p>
      </div>
    </div>
  )
}
